import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send } from "lucide-react";

export default function Messages() {
  const { otherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvos = () => api.get("/messages/conversations").then((r) => setConvos(r.data));
  const loadThread = () => otherId && api.get(`/messages/${otherId}`).then((r) => setThread(r.data));

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { loadThread(); }, [otherId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !otherId) return;
    await api.post("/messages", { recipient_id: otherId, text });
    setText("");
    loadThread(); loadConvos();
  };

  const activeName = convos.find((c) => c.other_id === otherId)?.other_name ||
    thread.find((m) => m.sender_id === otherId)?.sender_name || "Conversation";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display text-4xl font-bold tracking-tight mb-6">Messages</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
        {/* Conversations */}
        <div className="bg-white border border-black/10 rounded-2xl overflow-y-auto no-scrollbar">
          {convos.length === 0 && <p className="p-4 text-sm text-muted-foreground">No conversations yet.</p>}
          {convos.map((c) => (
            <button
              key={c.conversation_id}
              data-testid={`convo-${c.other_id}`}
              onClick={() => navigate(`/messages/${c.other_id}`)}
              className={`w-full text-left flex items-center gap-3 p-4 border-b border-black/5 hover:bg-black/5 transition-colors ${otherId === c.other_id ? "bg-black/5" : ""}`}
            >
              <Avatar className="w-10 h-10"><AvatarFallback className="bg-[#E3E8E1] text-sm">{c.other_name?.[0]}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{c.other_name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="md:col-span-2 bg-white border border-black/10 rounded-2xl flex flex-col">
          {!otherId ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-black/5 font-semibold">{activeName}</div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
                {thread.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`msg-${m.id}`} className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-[#0A0A0A] text-white rounded-br-sm" : "bg-[#F4F1EC] rounded-bl-sm"}`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-black/5 flex items-center gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 bg-[#F4F1EC] rounded-full px-4 py-2.5 text-sm outline-none" />
                <button data-testid="send-message-btn" className="bg-[#0A0A0A] text-white rounded-full p-2.5 hover:bg-[#FF5A00] transition-colors"><Send className="w-4 h-4" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
